// Utility functions for data export

export function exportToCSV(data: any[], filename: string) {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in values
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToJSON(data: any[], filename: string) {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.json`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Format data for different report types
export function formatAttendanceReport(records: any[]) {
  return records.map(record => ({
    Date: record.date,
    'Check In': record.checkIn || 'N/A',
    'Check Out': record.checkOut || 'N/A',
    Status: record.status,
    Location: record.location || 'N/A',
    'Break Time (min)': record.breakTime || 0,
  }));
}

export function formatLeaveReport(requests: any[]) {
  return requests.map(request => ({
    Employee: request.employeeName,
    'Leave Type': request.type,
    'Start Date': request.startDate,
    'End Date': request.endDate,
    'Total Days': request.totalDays,
    Reason: request.reason,
    Status: request.status,
    'Requested At': new Date(request.requestedAt).toLocaleDateString(),
  }));
}

export function formatTimesheetReport(entries: any[]) {
  return entries.map(entry => ({
    Date: entry.date,
    Project: entry.project,
    'Hours Worked': entry.hours,
    Description: entry.description || 'N/A',
  }));
}

export function formatEmployeeReport(employees: any[]) {
  return employees.map(emp => ({
    Name: emp.name,
    Email: emp.email,
    Position: emp.position,
    Department: emp.department,
    'Start Date': emp.startDate,
    Status: emp.status,
  }));
}

// Export all organization data as a ZIP-like JSON bundle
export function exportOrganizationData(orgData: {
  organization: any;
  employees: any[];
  departments: any[];
  locations: any[];
  attendance: any[];
  leaves: any[];
  timesheets: any[];
  shifts: any[];
  holidays: any[];
  performances: any[];
  documents: any[];
  expenses: any[];
  wfhRequests: any[];
}) {
  const exportBundle = {
    exportedAt: new Date().toISOString(),
    organizationId: orgData.organization?.id,
    organizationName: orgData.organization?.name,
    data: {
      organization: orgData.organization,
      employees: orgData.employees,
      departments: orgData.departments,
      locations: orgData.locations,
      attendance: orgData.attendance,
      leaveRequests: orgData.leaves,
      timesheetEntries: orgData.timesheets,
      shiftSchedules: orgData.shifts,
      holidays: orgData.holidays,
      performanceReviews: orgData.performances,
      documents: orgData.documents,
      expenseReimbursements: orgData.expenses,
      wfhRequests: orgData.wfhRequests,
    },
    metadata: {
      totalEmployees: orgData.employees?.length || 0,
      totalAttendanceRecords: orgData.attendance?.length || 0,
      totalLeaveRequests: orgData.leaves?.length || 0,
      totalTimesheetEntries: orgData.timesheets?.length || 0,
      exportFormat: 'FlowHR Organization Bundle v1.0',
    }
  };

  const jsonContent = JSON.stringify(exportBundle, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const filename = `${orgData.organization?.name || 'organization'}_data_export_${new Date().toISOString().split('T')[0]}`.replace(/\s+/g, '_');
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  return exportBundle.metadata;
}
